import { drizzle } from "drizzle-orm/d1";
import { project_api_keys, projects, schema, subscriptions } from "./schema";
import { and, desc, eq } from "drizzle-orm";
import type { D1Database } from "@cloudflare/workers-types";
import { hashProjectApiKey } from "./project-api-key-security";

export function createDB(binding: D1Database) {
  return drizzle(binding, { schema });
}

export type PrimaryDB = ReturnType<typeof createDB>;

export type VerifiedProjectApiKey = {
  keyId: string;
  userId: string;
  projectId: number;
  projectSlug: string;
};

export async function verifyProjectApiKey(
  db: PrimaryDB,
  apiKey: string,
  expectedProjectSlug?: string,
): Promise<VerifiedProjectApiKey | null> {
  const keyHash = await hashProjectApiKey(apiKey, "");
  const [row] = await db
    .select({
      keyId: project_api_keys.id,
      userId: project_api_keys.user_id,
      projectId: project_api_keys.project_id,
      projectSlug: projects.slug,
      expiresAt: project_api_keys.expires_at,
      revokedAt: project_api_keys.revoked_at,
    })
    .from(project_api_keys)
    .innerJoin(projects, eq(projects.id, project_api_keys.project_id))
    .where(eq(project_api_keys.key_hash, keyHash))
    .limit(1);

  const now = new Date();
  if (
    !row ||
    (row.revokedAt && row.revokedAt <= now) ||
    (row.expiresAt && row.expiresAt <= now) ||
    (expectedProjectSlug && row.projectSlug !== expectedProjectSlug)
  ) {
    return null;
  }

  return {
    keyId: row.keyId,
    userId: row.userId,
    projectId: row.projectId,
    projectSlug: row.projectSlug,
  };
}


export async function getUserActiveSubscription(
  userId: string,
  db: PrimaryDB,
) {
  const [currentSubscription] = await db
    .select()
    .from(subscriptions)
    .where(
      and(eq(subscriptions.user_id, userId), eq(subscriptions.status, "active")),
    )
    .orderBy(desc(subscriptions.createdAt))
    .limit(1);

  return currentSubscription;
}


// Re-export query operators from the same drizzle-orm instance used by this package.
export { eq } from "drizzle-orm";
export {
  generateProjectApiKey,
  hashProjectApiKey,
  hasProjectApiKeyPermissions,
} from "./project-api-key-security";
